import { useUserStore } from '@/store/user'
import router from '@/router/Router'

const getApiHost = () => {
  return process.env.VUE_APP_API_HOST || '';
};

// Read from the store, not localStorage: useStorage persists asynchronously
export const getToken = () => {
  return useUserStore().token || '';
};

export class ApiError extends Error {
  constructor(code, content) {
    super(`API Error ${code}`);
    this.name = 'ApiError';
    this.code = code;
    this.content = content;
  }
}

export const fetchMainApi = async (
  method,
  url,
  requestInit,
  contentType = 'application/json',
  includeAuth = true
) => {
  const apiHost = getApiHost();
  const baseUrl = apiHost ? `${apiHost}/api` : '/api';

  // Start with base headers
  const headers = {
    Accept: 'application/json',
  };

  // Add authorization if needed
  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  // Set content type
  if (contentType !== 'multipart/form-data') {
    headers['Content-Type'] = contentType;
  }

  // Merge with custom headers (these can override defaults)
  Object.assign(headers, requestInit?.headers);

  return fetch(url.startsWith('http') ? url : `${baseUrl}${url}`, {
    method,
    headers,
    credentials: 'include',
    ...requestInit
  })
    .then(async response => {
      if (response.ok) {
        return response.json();
      }

      const errorData = await response.json().catch(() => ({}));

      switch (response.status) {
        case 401: {
          useUserStore().cleanAll();

          // Redirect to login with current path (unless already on auth page)
          const currentPath = router.currentRoute.value.fullPath;
          const isAuthPage = currentPath.startsWith('/login') ||
            currentPath.startsWith('/register') ||
            currentPath.startsWith('/forgot-password') ||
            currentPath.startsWith('/auth/');

          if (!isAuthPage) {
            router.push({
              path: '/login',
              query: { redirect: currentPath }
            });
          }

          throw new ApiError('401', errorData);
        }
        case 403:
          throw new ApiError('403', errorData);
        case 404:
          throw new ApiError('404', errorData);
        case 422:
          throw new ApiError('422', errorData);
        default:
          throw new ApiError(
            String(response.status),
            errorData || 'Unexpected error'
          );
      }
    })
    .catch(e => {
      console.error('API Error:', e);

      if (e instanceof ApiError) {
        // Re-throw the ApiError content, ensuring it's always defined
        throw e.content || { message: 'API Error occurred', code: e.code };
      }

      // For network errors or other issues, throw a consistent error format
      if (e instanceof TypeError && e.message.includes('fetch')) {
        // Network error
        throw { message: 'Network error. Please check your connection.', code: 'NETWORK_ERROR' };
      }

      // Unknown error
      throw { message: e.message || 'An unexpected error occurred', code: 'UNKNOWN_ERROR' };
    });
};

const defaultFetcher =
  (domain) => (method, url, requestInit) =>
    fetch(domain + url, {
      method,
      headers: {
        ['Content-Type']: 'application/json',
        Accept: 'application/json'
      },
      ...requestInit
    });

export const fetchCalculatorApi = defaultFetcher(getApiHost());

// Re-export all API endpoints
export * from './auth.js'
export * from './locationEndpoints.js'
export * from './documentsEndpoints.js'
export * from './accountEndpoints.js'
export * from './bookmarks.js'
export * from './leadsEndpoints.js'
export * from './orders.js'
export * from './settingsEndpoints.js'
export * from './equipmentEndpoints.js'
export * from './paymentProfileEndpoints.js'
export * from './user.js'
export * from './offersEndpoints.js'
export * from './notificationSettingsEndpoints.js'
